import { ChevronDown, Gauge } from "lucide-react";
import { motion } from "framer-motion";
import { useState, useEffect } from "react";
import { useTheme } from "../context/ThemeContext";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import { Spin } from "antd";

export function CapacityPlanning({
  data: apiData,
  loading,
  selectedUnit,
  onUnitChange,
}) {
  const { theme, colors } = useTheme();
  // Define theme-specific colors
  const whiteThemeColors = [
    "#4A90E2",
    "#50E3C2",
    "#B8E986",
    "#FF6F61",
    "#f89256",
  ];
  const blueTealThemeColors = [
    "#4A90E2",
    "#50E3C2",
    "#B8E986",
    "#FF6F61",
    "#f89256",
  ];

  const chartColors =
    theme === "white" ? whiteThemeColors : blueTealThemeColors;

  const [showUnitDropdown, setShowUnitDropdown] = useState(false);

  // Function to parse the capacity values
  const parseCapacity = (value) => {
    if (!value) return 0;
    return Number(value.replace(/,/g, "").replace("K", "").trim());
  };

  // Map dropdown options to API-compatible values
  const dropdownOptions = [
    { label: "Gallons/Day", value: "gallons" },
    { label: "Equivalent connection – COH", value: "COH" },
    { label: "Equivalent connection – TCEQ", value: "TCEQ" },
  ];


  const yAxisUnit =
  selectedUnit === "gallons" ? "Gallons/Day" : "Connections";
  // Format chart data based on the API response
  const chartData = apiData
    ? [
        {
          id: "capacity-limit",
          label: apiData.total_limit_capacity_name,
          name: "Capacity Limit",
          value: parseCapacity(apiData.total_limit_capacity),
          unit: "K",
          colorIndex: 0,
        },
        {
          id: "total-capacity",
          label: apiData.total_capacity_name,
          name: "Total Cap.",
          value: parseCapacity(apiData.total_capacity),
          unit: "K",
          colorIndex: 1,
        },
        {
          id: "allocated",
          label: apiData.allocated_capacity_name,
          name: "Allocated",
          value: parseCapacity(apiData.allocated_capacity),
          unit: "K",
          colorIndex: 2,
        },
        {
          id: "avg-usage",
          label: apiData.total_flow_365_days_name,
          name: "Avg Usage\n(12 months)",
          value: parseCapacity(apiData.total_flow_365_days_sum),
          unit: "K",
          colorIndex: 3,
        },
        {
          id: "peak-usage",
          label: apiData.total_flow_365_days_max_sum_name,
          name: "Peak Usage\n(12 months)",
          value: parseCapacity(apiData.total_flow_365_days_max_sum),
          unit: "K",
          subLabel: apiData.total_flow_365_days_max_date,
          colorIndex: 4,
        },
      ]
    : [];

  // Custom tooltip component for the chart
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div
          className="rounded-lg p-3 shadow-xl border"
          style={{
            backgroundColor: theme === "white" ? "#ffffff" : colors.background,
            borderColor: colors.border,
          }}
        >
          <p
            className="text-sm font-semibold mb-1"
            style={{ color: colors.text }}
          >
            {data.label}
          </p>
          <p className="text-xs" style={{ color: colors.textSecondary }}>
            {Number(data.value).toLocaleString("en-US")}K {yAxisUnit}
          </p>
          {data.subLabel && (
            <p className="text-xs mt-1" style={{ color: colors.textSecondary }}>
              {data.subLabel}
            </p>
          )}
        </div>
      );
    }
    return null;
  };

  // Handle changing the unit
  const handleUnitChange = (unit) => {
    // Encode the unit for the API call
    const encodedUnit = encodeURIComponent(unit);
    onUnitChange(encodedUnit);
  };

  const CustomXAxisTick = ({ x, y, payload }) => {
    const lines = payload.value.split("\n");

    return (
      <g transform={`translate(${x},${y})`}>
        <text
          x={0}
          y={0}
          textAnchor="middle"
          fill={colors.textSecondary}
          fontSize={10}
        >
          {lines.map((line, index) => (
            <tspan key={index} x="0" dy={index === 0 ? "0.8em" : "1.2em"}>
              {line}
            </tspan>
          ))}
        </text>
      </g>
    );
  };
  

  return (
    <motion.div
      className="rounded-xl p-5 transition-colors duration-300 relative overflow-hidden group h-full flex flex-col"
      style={{
        backgroundColor: theme === "white" ? "#ffffff" : colors.glassEffect,
        backdropFilter: "blur(10px)",
        borderWidth: "1px",
        borderStyle: "solid",
        borderColor: colors.border,
      }}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.25 }}
      whileHover={{
        x: 10,
        transition: { type: "spring", stiffness: 300 },
      }}
    >
      {/* Animated top line on hover */}
      <div
        className="absolute top-0 left-0 h-[2px] w-0 group-hover:w-full transition-all duration-[400ms] ease-out"
        style={{ backgroundColor: colors.primary }}
      />

      <div className="relative z-10 flex flex-col h-full max-[567px]:overflow-x-scroll">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div
              className="p-2 rounded-lg"
              style={{ backgroundImage: colors.gradient }}
            >
              <Gauge className="w-4 h-4 text-white" />
            </div>
            <h3
              className="font-semibold tracking-tight text-[24px]"
              style={{ color: colors.text }}
            >
              Capacity Planning
            </h3>
          </div>

          {/* Dropdown for changing the unit */}
          <div className="relative">
            <button
              onClick={() => setShowUnitDropdown(!showUnitDropdown)}
              className="flex items-center gap-1.5 px-2.5 py-1 border rounded-lg cursor-pointer transition-all text-[10px] font-medium"
              style={{
                borderColor: showUnitDropdown ? colors.primary : colors.border,
                backgroundColor: showUnitDropdown
                  ? `${colors.primary}0D`
                  : "transparent",
                color: colors.textSecondary,
              }}
              onMouseEnter={(e) => {
                if (!showUnitDropdown) {
                  e.currentTarget.style.borderColor = colors.primary;
                  e.currentTarget.style.backgroundColor = `${colors.primary}0D`;
                }
              }}
              onMouseLeave={(e) => {
                if (!showUnitDropdown) {
                  e.currentTarget.style.borderColor = colors.border;
                  e.currentTarget.style.backgroundColor = "transparent";
                }
              }}
            >
              <span
                className="text-[13px]"
                style={{ textTransform: "capitalize" }}
              >
                {selectedUnit}
              </span>
              <ChevronDown className="w-3 h-3" />
            </button>

            {showUnitDropdown && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setShowUnitDropdown(false)}
                />
                <div
                  className="absolute top-full mt-2 right-0 border rounded-lg shadow-lg overflow-hidden z-20 min-w-[200px]"
                  style={{
                    backgroundColor:
                      theme === "white" ? "#ffffff" : colors.background,
                    borderColor: colors.border,
                  }}
                >
                  {dropdownOptions.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => {
                        handleUnitChange(option.value); // Pass the encoded value here
                        setShowUnitDropdown(false);
                      }}
                      className="w-full text-left px-3 py-2 text-[14px] transition-colors"
                      style={{
                        backgroundColor:
                          selectedUnit === option.value
                            ? `${colors.primary}1A`
                            : "transparent",
                        color:
                          selectedUnit === option.value
                            ? colors.primary
                            : colors.textSecondary,
                      }}
                      onMouseEnter={(e) => {
                        if (selectedUnit !== option.value) {
                          e.currentTarget.style.backgroundColor = `${colors.primary}0D`;
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (selectedUnit !== option.value) {
                          e.currentTarget.style.backgroundColor = "transparent";
                        }
                      }}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Bar Chart */}
        <div
          className="max-[567px]:w-[530px] flex items-center justify-center"
          style={{ height: "400px" }}
        >
          {loading ? (
            <div className="flex flex-col items-center gap-2">
              <Spin size="large" />
              <span
                style={{
                  color: colors.textSecondary,
                  fontSize: "13px",
                }}
              >
                Loading chart...
              </span>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={chartData}
                margin={{ top: 10, right: 10, left: 5, bottom: 0 }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke={theme === "white" ? "#e0e0e0" : `${colors.border}40`}
                  vertical={false}
                />
                <XAxis
                  dataKey="name"
                  stroke={colors.textSecondary}
                  tick={<CustomXAxisTick />}
                  axisLine={{ stroke: colors.border }}
                  interval={0}
                  tickMargin={10}
                  // angle={-45}
                  // textAnchor="center"
                  height={60}
                />
                <YAxis
                  stroke={colors.textSecondary}
                  tick={{ fill: colors.textSecondary, fontSize: 11 }}
                  axisLine={{ stroke: colors.border }}
                  // tickFormatter={(value) => `${value / 1000}K`}
                  tickFormatter={(value) => `${Number(value).toLocaleString("en-US")}K`}
                  width={80}
                    label={{
                      value: yAxisUnit,
                      angle: -90,
                      position: "insideLeft",
                      style: {
                        textAnchor: "middle",
                        fill: colors.textSecondary,
                        fontSize: 12,
                      },
                    }}
                />
                <Tooltip
                  content={<CustomTooltip />}
                  cursor={{ fill: `${colors.primary}10` }}
                />
                <Bar
                  dataKey="value"
                  radius={[8, 8, 0, 0]}
                  animationDuration={800}
                >
                  {chartData.map((entry, index) => (
                    <Cell
                      key={entry.id}
                      fill={chartColors[index % chartColors.length]} // Apply dynamic colors based on theme
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    </motion.div>
  );
}
